import { useEffect, useRef } from "react";
import { Key } from "ts-key-enum";
import { debounce } from "@evergis/sgis/es/utils/utils";

type KeyEventName = "keyup" | "keydown" | "keypress";

const KEY_EVENT: KeyEventName = "keydown";

type KeyEventHandler = (e: KeyboardEvent) => void;
type KeyActionsMap = Partial<Record<Key, Nullable<KeyEventHandler>>>;

type UseKeyOptions = {
  preventActions?: boolean;
};

export const useKey = (actionsMap: KeyActionsMap, { preventActions = true }: UseKeyOptions = {}) => {
  const keyHandlerRef = useRef<KeyEventHandler>(null);

  useEffect(() => {
    if (keyHandlerRef.current) {
      window.removeEventListener(KEY_EVENT, keyHandlerRef.current);
    }

    keyHandlerRef.current = debounce(e => {
      if (actionsMap[e.key] && !e.defaultPrevented) {
        if (preventActions) {
          e.preventDefault();
          e.stopPropagation();
        }
        actionsMap[e.key](e);
      }
    }, 0);

    window.addEventListener(KEY_EVENT, keyHandlerRef.current);

    return () => {
      window.removeEventListener(KEY_EVENT, keyHandlerRef.current);
    };
  }, Object.values(actionsMap));
};
